<template>
  <div
    class="vf-j115vr"
    :class="[
      $attrs.class as CSSClass,
      {
        'bg-white': !disabled,
        '[&>:first-child]:c-grey-30': $slots.select && disabled,
        'vf-2jed4o': $slots.select,
      },
      disabled ? 'bg-grey-90 b-grey-50' : invalid ? (variant ? 'b-red-60' : 'b-red-30') : 'b-grey-40',
    ]"
    data-test-id="vf-input"
    :style="$attrs.style as StyleValue"
  >
    <slot name="select" />
    <div class="grid cols-[1fr_auto] grow" data-test-id="vf-input-field">
      <label
        v-bind="attrs"
        class="vf-39l72j"
        :class="[
          disabled
            ? 'vf-ltd332'
            : 'vf-svn3uk',
          {
            'pr-10': isIconShown || $slots.end,
            'pr-18': isIconShown && $slots.end,
          },
        ]"
      >
        <span
          v-if="$slots.default"
          class="col-start-1 col-end-2 form-label"
          :class="disabled ? 'c-grey-30' : invalid ? 'c-red-30' : 'c-grey-20'"
          data-test-id="vf-input-label"
        >
          <!-- @slot Slot is meant for `label` only -->
          <slot />
          <span v-if="required" aria-hidden="true"> *</span>
        </span>
        <slot name="input">
          <!-- @slot Slot is meant for passing `input` element for lower level configurations -->
          <base-input
            v-bind="$props"
            v-model="modelValue"
            :aria-describedby="$attrs['aria-describedby']"
            class="focus-visible:outline-none"
            :type
            @change="$emit('change', $event)"
            @focus="$emit('focus', $event)"
          />
        </slot>
      </label>
      <div class="pointer-within col-start-2 row-start-1 flex self-center gap-1 pr-3">
        <!-- @slot Slot is meant for `icon` to the right of input. But may be used for any content -->
        <slot name="end" />
        <vf-icon
          v-show="isIconShown"
          class="pointer-events-none"
          :class="invalid ? 'c-red-30' : 'c-green-30'"
          :name="invalid ? 'error' : 'success'"
          size="md"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'
import type { StyleValue } from 'vue'

defineOptions({
  inheritAttrs: false
})

const {
  type = 'text',
  /**
   * Needs to be explicitly set to `undefined` for indeterminate validation state
   */
  invalid = undefined
} = defineProps<{
  /**
   * Defines input name
   */
  name?: string
  /**
   * Defines type of the input
   */
  type?: 'text' | 'email' | 'number' | 'search' | 'tel' | 'password' | 'date'
  /**
   * Defines placeholder when input is empty
   */
  placeholder?: string
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines validation state
   * There are 3 possible states
   * Indeterminate === undefined (no validation icons visible)
   * Valid === true
   * Invalid === false
   */
  invalid?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
  /**
   * Defines if the input is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines the minimum value that is acceptable
   * Works with types: number, date
   */
  min?: string
  /**
   * Defines the maximum value that is acceptable
   * Works with types: number, date
   */
  max?: string
}>()

defineEmits<{
  change: [value: string]
  focus: [value: string]
}>()

const modelValue = defineModel<string | number>()

const { showErrorIcon, showSuccessIcon } = useAppConfig().components.vf.input
const { class: _, 'aria-describedby': __, ...attrs } = useAttrs()

const isIconShown = computed(() => {
  if (showErrorIcon && showSuccessIcon) return invalid !== undefined
  if (showErrorIcon) return invalid === true
  if (showSuccessIcon) return invalid === false
})
</script>
